import * as React from 'react';
import PageHeader from 'components/common/PageHeader';
import { Route, Switch, Redirect, withRouter, RouteComponentProps } from 'react-router';
import * as helpers from '../../utils/helpers'; 
import { userHasPermission } from '../../utils/helpers';
import * as router from '../../services/router-service';
import AIDesignerContainer from './designer/AIDesignerContainer';
import AICatalogContainer from './catalog/AICatalogContainer';

interface Props extends RouteComponentProps {}

interface State {
    activeTab: string;
    availableTabs: string[]; 
}

class AILabIndex extends React.PureComponent<Props, State> {
    public state: State = {
        activeTab: "",
        availableTabs: []
    }    

    // Reactively monitors URL route transitions to ensure header tab highlighting tracks correctly
    public componentDidUpdate(prevProps: Props) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            this.syncActiveTabWithRoute();
        }
    }

    // Synchronizes top tab selection header highlight states based on the browser address string
    private syncActiveTabWithRoute = () => {
        const currentPath = this.props.location.pathname;
        let activeTab = "";

        if (currentPath.includes('/ai-lab/designer')) {
            activeTab = "AI Tool Designer";
        } else if (currentPath.includes('/ai-lab/catalog')) {
            activeTab = "AI Tool Catalog";
        } else {
            const tabs = this.getAvailableTabs();
            if (tabs.length > 0) {
                activeTab = tabs[0];
            }
        }

        this.setState({ activeTab });
    }

    private getAvailableTabs = (): string[] => {
        const tabs: string[] = [];
        
        if (userHasPermission("AI System Designer")) {
            tabs.push("AI Tool Designer");
        }

        const allPerms = helpers.getUserPermissions() || [];
        const hasToolAccess = allPerms.some(p => p.startsWith("AI_TOOL_"));
        
        if (hasToolAccess || userHasPermission("AI System Designer")) {
            tabs.push("AI Tool Catalog");
        }

        return tabs;
    }

    public componentDidMount(): void {
        const userInfo = helpers.getUserInfo(); 
        if(!userInfo) {
            localStorage.clear(); 
            window.location.href = '/logout'; 
            return;
        }

        const tabs = this.getAvailableTabs();
        this.setState({ availableTabs: tabs }, () => {
            this.syncActiveTabWithRoute();
        });
    }

    // Handles tab switches when clicking directly on the PageHeader UI row elements
    public handleTabs = (name: string) => {
        this.setState({ activeTab: name });

        if (name === "AI Tool Designer") {
            router.goto('/ai-lab/designer');
        } else if (name === "AI Tool Catalog") {
            router.goto('/ai-lab/catalog');
        }
    };

    public render() {
        const { activeTab, availableTabs } = this.state; 
        const tabs = this.getAvailableTabs();
        const defaultRedirectPath = tabs[0] === "AI Tool Designer" ? "/ai-lab/designer" : "/ai-lab/catalog";

        return (
            <div className="ai-lab-container">
                {/* The tab navigation row stays fixed on top at all times now! */}
                <div style={{ visibility: availableTabs.length > 1 ? "visible" : "hidden" }}>
                    <PageHeader
                        activeTab={activeTab}
                        getTabName={this.handleTabs}
                        tabs={availableTabs}
                    />
                </div>                
                
                {/* Nested Inner Content Routing Block swapping child containers underneath the common header tabs */}
                <div className="ai-lab-content">
                    {availableTabs.length === 0 ? (
                        <div className="p-5 text-center">
                            <p className="text-muted">You do not have access to any AI tools. Please contact your administrator.</p>
                        </div>
                    ) : (
                        <Switch>
                            {/* Inner catalogs match and display inside our common shell environment container */}
                            <Route exact path="/ai-lab/catalog" component={AICatalogContainer} />
                            <Route path="/ai-lab/designer/:toolId?" component={AIDesignerContainer} />
                            
                            {/* If a user hits bare /ai-lab, redirect seamlessly inside the switch layout tree */}
                            <Redirect from="/ai-lab" to={defaultRedirectPath} />
                        </Switch>
                    )}
                </div>
            </div>
        );
    }
}
export default withRouter(AILabIndex);